import type { LucideIcon, LucideProps } from 'lucide-react';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

/**
 * Lucide 아이콘 래퍼 — D2 §8.
 *
 * 프로토타입이 쓰던 이모지 22종을 대체한다. 이모지는 플랫폼마다 다르게
 * 렌더링되고 D1 §M1 "조용한 권위"와 충돌한다.
 *
 * 이 래퍼를 쓰는 이유는 크기와 획 굵기를 고정하기 위해서다.
 * `lucide-react`를 직접 import해서 쓰면 기본 stroke 2가 적용되어
 * 화면이 무거워진다. 항상 이 컴포넌트를 거친다.
 *
 * ```tsx
 * import { FileText } from 'lucide-react';
 * <Icon as={FileText} size="sm" />
 * ```
 */
export const ICON_SIZE = {
  /** 16px — 인라인 · 칩 내부 */
  sm: 16,
  /** 20px — 기본. 버튼 · 리스트 */
  md: 20,
  /** 24px — 헤더 · 빈 상태 */
  lg: 24,
} as const;

export type IconSize = keyof typeof ICON_SIZE;

export interface IconProps extends Omit<LucideProps, 'size' | 'ref'> {
  /** Lucide 아이콘 컴포넌트 */
  as: LucideIcon;
  size?: IconSize;
}

export function Icon({ as: LucideGlyph, size = 'md', className, ...props }: IconProps) {
  return (
    <LucideGlyph
      size={ICON_SIZE[size]}
      // Lucide 기본값 2보다 얇다 — D1 §M1
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      {...props}
    />
  );
}

/**
 * 의미를 전달하는 아이콘(아이콘만 있는 상태 표시 등)에 접근 가능한 이름을 붙인다.
 * 장식용이면 `Icon`을 그대로 쓰고, 뜻이 있으면 이 컴포넌트로 감싼다 — L2-06 §8.
 *
 * `role="img" + aria-label` 대신 화면에 보이지 않는 텍스트를 함께 렌더한다.
 * 스크린리더·번역기·검색 어디에서나 동작하기 때문이다.
 */
export interface IconLabelProps extends ComponentProps<'span'> {
  label: string;
}

export function IconLabel({ label, children, className, ...props }: IconLabelProps) {
  return (
    <span className={cn('inline-flex items-center', className)} {...props}>
      {children}
      <span className="sr-only">{label}</span>
    </span>
  );
}
